/**
 * Put a person the server has just confirmed onto the invoice page's cached read, so the Bill To recipients list
 * and the "Send this invoice to" card show them at once (SPEC-christa-contacts-identity R1 AC18, finding o).
 *
 * The add endpoint answers with the saved invoice_contacts row. The page used to wait for a full re-read of the
 * invoice (many queries on the server) before either list changed, so a slow read looked like "nothing happened".
 * This applies the server's own answer to the cached read first; the re-read that follows still replaces the
 * whole thing, so the server stays the source of truth and any difference (for example the order it chose)
 * is corrected a moment later.
 *
 * Pure: takes the cached value, returns a new one, never mutates.
 */

export interface AddedInvoiceContact {
  id: string;
  contact_id?: string | null;
  contact_name: string;
  contact_email: string;
  role: string;
}

type Role = 'to' | 'cc';

/** TO rows first (the server's read order), the new row last inside its own role group. */
function insertByRole<T>(rows: T[], row: T, roleOf: (r: T) => string, role: Role): T[] {
  if (role === 'cc') return [...rows, row];
  let lastTo = -1;
  rows.forEach((r, i) => { if (roleOf(r) === 'to') lastTo = i; });
  return [...rows.slice(0, lastTo + 1), row, ...rows.slice(lastTo + 1)];
}

interface LookupRow { name: string | null; email: string | null; role: string; missing_email: boolean }

export function withAddedRecipient<T extends Record<string, unknown> | undefined>(full: T, added: AddedInvoiceContact): T {
  if (!full) return full;
  const data = full as Record<string, unknown>;
  const role: Role = added.role === 'to' ? 'to' : 'cc';

  const meta = (data.recipients_meta as Array<Record<string, unknown>> | undefined) ?? [];
  // Already there (the re-read beat us, or this ran twice): nothing to add.
  if (meta.some((m) => m.invoice_contact_id === added.id)) return full;

  const metaRow = {
    invoice_contact_id: added.id,
    contact_id: added.contact_id ?? null,
    name: added.contact_name,
    email: added.contact_email,
    role,
    source: added.contact_id ? 'snapshot_org' : 'typed_in',
    is_dba_accounting: false,
    is_org_accounting: false,
    is_project_default: false,
  };
  const invoiceContacts = (data.invoice_contacts as Array<Record<string, unknown>> | undefined) ?? [];
  const next: Record<string, unknown> = {
    ...data,
    recipients_meta: insertByRole(meta, metaRow, (r) => String(r.role), role),
    invoice_contacts: insertByRole(
      invoiceContacts,
      { id: added.id, contact_id: added.contact_id ?? undefined, contact_name: added.contact_name,
        contact_email: added.contact_email, role },
      (r) => String(r.role),
      role,
    ),
  };

  const toMeta = data.to_contacts_meta as { explicit_count: number; fallback_active: boolean } | null | undefined;
  if (toMeta) {
    next.to_contacts_meta = {
      ...toMeta,
      explicit_count: toMeta.explicit_count + (role === 'to' ? 1 : 0),
      fallback_active: role === 'to' ? false : toMeta.fallback_active,
    };
  }

  const lookup = data.recipient_lookup as
    | (Record<string, unknown> & { recipients: LookupRow[]; recorded_on_invoice: boolean; to_unresolved?: unknown })
    | null
    | undefined;
  if (lookup) {
    const row: LookupRow = { name: added.contact_name, email: added.contact_email, role, missing_email: !added.contact_email };
    // A lookup that was only INFERRING people (the project's saved set, the client defaults, the last sent
    // invoice) stops inferring once the invoice records someone of its own: the recorded rows are the answer.
    const base = lookup.recorded_on_invoice ? lookup.recipients : [];
    const rows = insertByRole(base, row, (r) => r.role, role);
    const emails = (r: LookupRow[]) => r.filter((x) => x.email).map((x) => x.email as string);
    next.recipient_lookup = {
      ...lookup,
      recipients: rows,
      origin: 'invoice',
      origin_label: 'recorded on this invoice',
      recorded_on_invoice: true,
      copy_text: emails(rows).join('; '),
      copy_text_to: emails(rows.filter((r) => r.role !== 'cc')).join('; '),
      copy_text_cc: emails(rows.filter((r) => r.role === 'cc')).join('; '),
      emailable_count: emails(rows).length,
      empty_reason: null,
      to_unresolved: role === 'to' ? null : lookup.to_unresolved ?? null,
    };
  }
  return next as T;
}

/** TO group first, each group keeping its order (the server's read order). */
function sortByRole<T>(rows: T[], roleOf: (r: T) => string): T[] {
  return [...rows.filter((r) => roleOf(r) === 'to'), ...rows.filter((r) => roleOf(r) !== 'to')];
}

/**
 * Show a role the server has just confirmed (SPEC-christa-contacts-identity R2 AC13) in every list that renders from
 * the invoice page's cached read: the Bill To recipients list, the "Send this invoice to" card and the TO count.
 * The re-read that follows still replaces the whole thing. Pure.
 */
export function withChangedRecipientRole<T extends Record<string, unknown> | undefined>(
  full: T, rowId: string, newRole: string,
): T {
  if (!full) return full;
  const data = full as Record<string, unknown>;
  const role: Role = newRole === 'to' ? 'to' : 'cc';

  const meta = (data.recipients_meta as Array<Record<string, unknown>> | undefined) ?? [];
  const changed = meta.find((m) => m.invoice_contact_id === rowId);
  if (!changed) return full;
  const email = String(changed.email ?? '').trim().toLowerCase();

  const next: Record<string, unknown> = { ...data };
  next.recipients_meta = sortByRole(
    meta.map((m) => (m.invoice_contact_id === rowId ? { ...m, role } : m)), (r) => String(r.role));
  const invoiceContacts = data.invoice_contacts as Array<Record<string, unknown>> | undefined;
  if (invoiceContacts) {
    next.invoice_contacts = sortByRole(
      invoiceContacts.map((c) => (c.id === rowId ? { ...c, role } : c)), (r) => String(r.role));
  }
  const toCount = (next.recipients_meta as Array<Record<string, unknown>>).filter((m) => m.role === 'to').length;
  const toMeta = data.to_contacts_meta as { explicit_count: number; fallback_active: boolean } | null | undefined;
  if (toMeta) {
    next.to_contacts_meta = { ...toMeta, explicit_count: toCount, fallback_active: toCount > 0 ? false : toMeta.fallback_active };
  }
  const lookup = data.recipient_lookup as
    | (Record<string, unknown> & { recipients: LookupRow[]; to_unresolved?: unknown })
    | null
    | undefined;
  if (lookup) {
    const rows = sortByRole(
      lookup.recipients.map((r) => ((r.email ?? '').trim().toLowerCase() === email && email ? { ...r, role } : r)),
      (r) => r.role);
    const emails = (r: LookupRow[]) => r.filter((x) => x.email).map((x) => x.email as string);
    next.recipient_lookup = {
      ...lookup,
      recipients: rows,
      copy_text: emails(rows).join('; '),
      copy_text_to: emails(rows.filter((r) => r.role !== 'cc')).join('; '),
      copy_text_cc: emails(rows.filter((r) => r.role === 'cc')).join('; '),
      to_unresolved: rows.some((r) => r.role === 'to') ? null : lookup.to_unresolved ?? null,
    };
  }
  return next as T;
}

/**
 * Show a typed-in person the server has just saved for the project (SPEC-christa-contacts-identity R2 AC14): the row
 * stops being a one-off in the same render, with its Save buttons gone. Pure.
 */
export function withPromotedRecipient<T extends Record<string, unknown> | undefined>(
  full: T, rowId: string, contactId: string, savedForProject: boolean,
): T {
  if (!full) return full;
  const data = full as Record<string, unknown>;
  const meta = data.recipients_meta as Array<Record<string, unknown>> | undefined;
  if (!meta || !meta.some((m) => m.invoice_contact_id === rowId)) return full;
  const next: Record<string, unknown> = {
    ...data,
    recipients_meta: meta.map((m) => (m.invoice_contact_id === rowId
      ? { ...m, contact_id: contactId, source: 'snapshot_org', is_project_default: savedForProject || Boolean(m.is_project_default) }
      : m)),
  };
  return next as T;
}
